import { useQuery } from '@tanstack/react-query'
import authAxios from '@/shared/api/authAxios'
import { Account } from '@/widgets/accountsGrid'
import { AGENT_STATUS_TEXTS, AGENT_TYPE_TEXTS } from './transferTypes'
import { buildReceiverHostUrl } from '@/shared/lib/buildReceiverHostUrl'
import { getSessionAbortSignal } from '@/shared/api/sessionAbortController'

const STALE_TIME = 5 * 60 * 1000

const transformAgentsDataToAccounts = (agentsData: any[]): Account[] => {
  const agentsMap = new Map()
  agentsData.forEach((agent) => agentsMap.set(agent.id, agent))

  const buildPath = (agent: any): string => {
    const pathParts: string[] = []
    let currentAgent = agent
    while (currentAgent) {
      pathParts.unshift(currentAgent.name)
      if (currentAgent.parentId && agentsMap.has(currentAgent.parentId)) {
        currentAgent = agentsMap.get(currentAgent.parentId)
      } else {
        break
      }
    }
    return pathParts.join(' > ')
  }

  return agentsData.map((agent) => ({
    id: agent.id,
    title: agent.name,
    username: agent.fullName || agent.name,
    path: buildPath(agent),
    type:
      AGENT_TYPE_TEXTS[agent.agentInfoType as keyof typeof AGENT_TYPE_TEXTS] ||
      AGENT_TYPE_TEXTS.default,
    objects: '0/0',
    status:
      AGENT_STATUS_TEXTS[agent.status as keyof typeof AGENT_STATUS_TEXTS] ||
      AGENT_STATUS_TEXTS.default,
  }))
}

interface UseAgentsInfoQueryParams {
  username: string | undefined
  userPassword: string | null | undefined
  currentServer: string | undefined
}

export const useAgentsInfoQuery = ({
  username,
  userPassword,
  currentServer,
}: UseAgentsInfoQueryParams) =>
  useQuery({
    queryKey: ['agents-info', username, currentServer],
    enabled: !!username && !!currentServer,
    queryFn: () => {
      const body = {
        loginPassword: { login: username || '', password: userPassword || '' },
        auth: '',
        agentId: '',
        useAsRoot: true,
        receiverHost: `${buildReceiverHostUrl(currentServer!)}/`,
      }
      return authAxios.post('/public/AgentsInfo', body, {
        timeout: 120_000,
        signal: getSessionAbortSignal(),
      })
    },
    staleTime: STALE_TIME,
    select: (res) => transformAgentsDataToAccounts(res.data),
  })
